import type { ReactNode } from 'react';
import type { MapFilters } from './types';
import { countActiveFilters } from './types';

export interface FilterOptions {
  types: string[];
  subtypes: string[];
  classes: string[];
  submarkets: string[];
  counties: string[];
  cities: string[];
  tenancies: string[];
  pipelineStatuses: string[];
}

interface Props {
  filter: MapFilters;
  onChange: (f: MapFilters) => void;
  onClose: () => void;
  onReset: () => void;
  resultCount: number;
  options: FilterOptions;
}

const titleCase = (s: string) => s.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
const digits = (v: string) => v.replace(/[^0-9]/g, '');
const decimal = (v: string) => v.replace(/[^0-9.]/g, '');

export default function MapFiltersPanel({ filter, onChange, onClose, onReset, resultCount, options }: Props) {
  const set = (k: keyof MapFilters, v: string) => onChange({ ...filter, [k]: v });
  const active = countActiveFilters(filter);

  // Inline function helpers (NOT components — defining components in render would
  // remount inputs on each keystroke and drop focus).
  const field = (label: string, control: ReactNode, span = false) => (
    <label className={`block ${span ? 'col-span-2' : ''}`}>
      <span className="text-xs font-medium text-gray-600">{label}</span>
      <div className="mt-1">{control}</div>
    </label>
  );

  const select = (k: keyof MapFilters, opts: string[]) => (
    <select value={filter[k]} onChange={(e) => set(k, e.target.value)}
      className="w-full border border-gray-300 rounded-md px-2.5 py-2 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-primary-500">
      <option value="">Select</option>
      {opts.map((o) => <option key={o} value={o}>{titleCase(o)}</option>)}
    </select>
  );

  const range = (kMin: keyof MapFilters, kMax: keyof MapFilters, ph = '', clean = digits) => (
    <div className="flex items-center gap-2">
      <input inputMode="decimal" placeholder={`${ph}Min`} value={filter[kMin]} onChange={(e) => set(kMin, clean(e.target.value))}
        className="w-full border border-gray-300 rounded-md px-2.5 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500" />
      <span className="text-gray-400">–</span>
      <input inputMode="decimal" placeholder={`${ph}Max`} value={filter[kMax]} onChange={(e) => set(kMax, clean(e.target.value))}
        className="w-full border border-gray-300 rounded-md px-2.5 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500" />
    </div>
  );

  // Segmented toggle (CoStar's Single/Multiple style). Clicking the active one clears it.
  const segmented = (k: keyof MapFilters, opts: { value: string; label: string }[]) => (
    <div className="inline-flex w-full rounded-md border border-gray-300 overflow-hidden">
      {opts.map((o, i) => (
        <button key={o.value} type="button" onClick={() => set(k, filter[k] === o.value ? '' : o.value)}
          className={`flex-1 px-3 py-2 text-sm transition-colors ${i > 0 ? 'border-l border-gray-300' : ''} ${filter[k] === o.value ? 'bg-primary-500 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}>
          {o.label}
        </button>
      ))}
    </div>
  );

  const caption = (t: string) => (
    <p className="col-span-2 text-sm font-bold text-gray-900 pt-4 pb-0.5 border-b border-gray-100 mb-1">{t}</p>
  );

  return (
    <div className="fixed inset-0 z-[1600] flex justify-end" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative w-full max-w-md bg-white h-full shadow-2xl flex flex-col">
        {/* Header */}
        <div className="shrink-0 flex items-center justify-between px-4 py-3 border-b">
          <h2 className="text-base font-semibold text-gray-900 inline-flex items-center gap-2">
            <svg className="w-4 h-4 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4h18M6 8h12M9 12h6M11 16h2" /></svg>
            Filters{active ? <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-primary-500 text-white text-[11px]">{active}</span> : null}
          </h2>
          <button onClick={onClose} className="p-1.5 rounded-lg text-gray-400 hover:bg-gray-100" aria-label="Close filters">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-4 py-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-3">
            {caption('Property')}
            {field('Property Type', select('type', options.types))}
            {field('Subtype', select('subtype', options.subtypes))}
            {field('Building Class', select('building_class', options.classes))}
            {field('Tenancy', segmented('tenancy', [{ value: 'Single', label: 'Single' }, { value: 'Multi', label: 'Multi' }, { value: 'Unknown', label: 'Unknown' }]))}
            {field('Status', select('pipeline_status', options.pipelineStatuses), true)}
            {field('Zoning contains', (
              <input value={filter.zoning} onChange={(e) => set('zoning', e.target.value)} placeholder="e.g. M-1, C-2"
                className="w-full border border-gray-300 rounded-md px-2.5 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500" />
            ), true)}
            {field('Expiring tenant', (
              <select value={filter.expiring_tenant} onChange={(e) => set('expiring_tenant', e.target.value)}
                className="w-full border border-gray-300 rounded-md px-2.5 py-2 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-primary-500">
                <option value="">Any</option>
                <option value="6">≤ 6 months (urgent)</option>
                <option value="12">≤ 12 months (prime)</option>
                <option value="18">≤ 18 months (early)</option>
              </select>
            ), true)}

            {caption('Location')}
            {field('Submarket', select('submarket', options.submarkets))}
            {field('County', select('county', options.counties))}
            {field('City', select('city', options.cities), true)}

            {caption('Financials')}
            {field('Sale Price', range('min_price', 'max_price', '$'))}
            {field('Sale Price / SF', range('min_psf', 'max_psf', '$'))}
            {field('Cap Rate (%)', range('min_cap', 'max_cap', '', decimal))}
            {field('Days on Market', range('min_dom', 'max_dom'))}

            {caption('Size & Age')}
            {field('Building Size (SF)', range('min_building_size', 'max_building_size'))}
            {field('Lot Size (acres)', range('min_lot_acres', 'max_lot_acres', '', decimal))}
            {field('Year Built', range('year_built_min', 'year_built_max'))}

            {caption('Show only')}
            <div className="col-span-2">
              <button type="button" onClick={() => set('has_photos', filter.has_photos ? '' : 'yes')}
                className={`px-3 py-1.5 text-sm rounded-full border transition-colors ${filter.has_photos ? 'bg-primary-500 text-white border-primary-500' : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'}`}>
                Has Photos
              </button>
            </div>
          </div>
          <div className="h-2" />
        </div>

        {/* Footer */}
        <div className="shrink-0 flex items-center gap-3 px-4 py-3 border-t bg-gray-50">
          <button onClick={onReset} className="px-4 py-2.5 text-sm text-gray-600 hover:text-gray-900 rounded-lg border border-gray-300 bg-white">Clear</button>
          <button onClick={onClose} className="flex-1 px-5 py-2.5 rounded-lg bg-primary-500 text-white text-sm font-semibold hover:bg-primary-600">
            Show {resultCount.toLocaleString()} result{resultCount === 1 ? '' : 's'}
          </button>
        </div>
      </div>
    </div>
  );
}
